import React, { useEffect, useState } from "react";
import { Select, toFixed, DateInput } from "../CustomComponent";
import {
  DEFAULT_INCOME_TAG_COLOR,
  emptyIncomeRow,
  formatNumberInput,
  formBlockStyle,
  INCOME_TAG_COLORS,
  incomeTagColor,
  parseNumberInput,
  RD_GREEN,
  RD_GREEN_DARK,
  RD_GREEN_SOFT,
  WHT_QUICK_RATES,
} from "./constants";

const num = (value) => Number(value) || 0;

// การ์ดรายการแบบกระชับ: ป้ายชื่อเล็ก ช่องกรอกขนาดเล็ก จบใน 2 บรรทัดบนจอกว้าง
const rowCardStyle = { background: "#ffffff", border: "1px solid #e2e8ec", borderRadius: 12 };
const labelStyle = { fontSize: "0.7rem", color: "#6b8198", fontWeight: 500, letterSpacing: "0.01em", minHeight: 16 };
const inputStyle = { borderRadius: 8, fontSize: "0.85rem" };

const Field = ({ label, col, hint, children }) => (
  <div className={col}>
    <div className="d-flex align-items-center mb-1" style={labelStyle}>{label}</div>
    {children}
    {hint ? <div style={{ fontSize: "0.65rem", color: "#94a3b3" }}>{hint}</div> : null}
  </div>
);

// หน้ากรอกเงินได้รายประเภท หน้าตาเดียวกับหน้า "ระบุข้อมูล" ของ e-Filing
// แก้ตรงไหนระบบบันทึกให้เองเมื่อหยุดพิมพ์ ไม่ต้องกดบันทึก
// payrollRows = รายการที่ระบบดึงจากสลิปเงินเดือนให้ แก้ไม่ได้ ต้องไปแก้ที่รอบเงินเดือน
// orderKeys = ลำดับรวมจากเซิร์ฟเวอร์ (uid ของรายการกรอกเอง / key ของรายการจากสลิป) onReorder(keys) = บันทึกลำดับใหม่
export default function IncomeDetail({ type, rows, setRows, payrollRows, orderKeys = [], onReorder, onBack }) {
  const [showCalc, setShowCalc] = useState(false);

  const setRowField = (key, field, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const removeRow = (key) => setRows((prev) => prev.filter((row) => row.key !== key));
  const addRow = () => setRows((prev) => [...prev, emptyIncomeRow()]);

  // ---- ลำดับการ์ดในฟอร์ม (รวมรายการจากสลิปและรายการที่กรอกเอง) ลากได้ทุกใบ ----
  // key ที่ใช้ลาก: รายการจากสลิป = item.key (payroll:/projection:) รายการกรอกเอง = row.key
  const payrollByKey = payrollRows.reduce((acc, item) => (item.key ? { ...acc, [item.key]: item } : acc), {});
  const rowByKey = rows.reduce((acc, row) => ({ ...acc, [row.key]: row }), {});
  const [order, setOrder] = useState([]);

  // ซิงก์ลำดับกับรายการที่มีอยู่: คงลำดับที่ลากไว้ในฟอร์ม เติมรายการใหม่ต่อท้าย ตัดรายการที่ถูกลบออก
  useEffect(() => {
    const available = [...payrollRows.map((item) => item.key).filter(Boolean), ...rows.map((row) => row.key)];
    const fromServer = orderKeys
      .map((key) => (payrollByKey[key] ? key : rows.find((row) => row.uid === key)?.key))
      .filter((key) => key && available.includes(key));
    setOrder((prev) => {
      const kept = prev.filter((key) => available.includes(key));
      const next = [...kept, ...fromServer.filter((key) => !kept.includes(key)), ...available.filter((key) => !kept.includes(key) && !fromServer.includes(key))];
      return next.length === prev.length && next.every((key, index) => key === prev[index]) ? prev : next;
    });
    // payrollByKey ผูกกับ payrollRows อยู่แล้ว
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payrollRows, rows, orderKeys]);

  // การ์ดใบไหนกำลังเปิดเมนูเลือกสีอยู่ (เปิดได้ทีละใบ) คลิกที่อื่นในหน้าจอแล้วปิด
  const [colorMenuKey, setColorMenuKey] = useState("");

  useEffect(() => {
    if (!colorMenuKey) return undefined;
    const close = () => setColorMenuKey("");
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [colorMenuKey]);

  const [drag, setDrag] = useState({ key: "", overKey: "", position: "" });
  // การ์ดจะลากได้เฉพาะตอนที่กดค้างที่ grip อยู่ ไม่งั้นลากเลือกข้อความใน input ข้างในไม่ได้
  const [armedKey, setArmedKey] = useState("");

  const handleDragStart = (event, key) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", key);
    setDrag({ key, overKey: "", position: "" });
  };

  const handleDragOver = (event, key) => {
    if (!drag.key || drag.key === key) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const position = event.clientY - rect.top < rect.height / 2 ? "before" : "after";
    if (drag.overKey !== key || drag.position !== position) setDrag((current) => ({ ...current, overKey: key, position }));
  };

  const handleDragEnd = () => {
    setDrag({ key: "", overKey: "", position: "" });
    setArmedKey("");
  };

  const handleDrop = (event, targetKey) => {
    event.preventDefault();
    const { key: sourceKey, position } = drag;
    handleDragEnd();
    if (!sourceKey || sourceKey === targetKey) return;

    const next = order.filter((key) => key !== sourceKey);
    const targetIndex = next.indexOf(targetKey);
    if (targetIndex < 0) return;
    next.splice(position === "after" ? targetIndex + 1 : targetIndex, 0, sourceKey);
    if (next.every((key, index) => key === order[index])) return;

    setOrder(next);
    // เรียง rows ให้ตรงกับลำดับใหม่ด้วย ตอนบันทึกอัตโนมัติ sort_order จะได้ตรงกัน
    const manualKeys = next.filter((key) => rowByKey[key]);
    setRows((prev) => [...prev].sort((a, b) => manualKeys.indexOf(a.key) - manualKeys.indexOf(b.key)));
    // ส่งเป็น key ฝั่งเซิร์ฟเวอร์: รายการกรอกเองใช้ uid ถ้ายังไม่มี uid ส่ง key ชั่วคราวไปให้ฝั่งพาเนลจับคู่หลังบันทึก
    onReorder?.(next.map((key) => (rowByKey[key] ? rowByKey[key].uid || key : key)));
  };

  const dropIndicatorStyle = (key) => {
    if (drag.overKey !== key) return {};
    return drag.position === "before" ? { borderTop: "3px solid #4caf50" } : { borderBottom: "3px solid #4caf50" };
  };

  const payrollGross = payrollRows.reduce((sum, item) => sum + num(item.amount), 0);
  const payrollWht = payrollRows.reduce((sum, item) => sum + num(item.wht_amount), 0);
  const manualGross = rows.reduce((sum, row) => sum + num(row.amount), 0);
  const manualWht = rows.reduce((sum, row) => sum + num(row.wht_amount), 0);

  const gross = payrollGross + manualGross;
  const rate = type?.expense?.rate || 0;
  const cap = type?.expense?.max || null;
  const standardExpense = cap ? Math.min((gross * rate) / 100, cap) : (gross * rate) / 100;

  // การ์ดรายการ 1 ใบ ใช้หน้าตาเดียวกันทั้งรายการที่กรอกเองและรายการจากสลิปเงินเดือน
  // readOnly = ช่องทั้งหมด disabled ไม่มีปุ่มลบ/ลาก แต่ยังนับเลขลำดับต่อเนื่องกัน จะได้ไล่ดูลำดับง่าย
  const renderCard = ({ key, index, values, row, readOnly = false, sourceLabel = "" }) => {
    const set = (name) => (event) => (readOnly ? undefined : setRowField(row.key, name, event.target.value));
    // ช่องเงิน: หน้าจอโชว์คั่นหลักพัน (1,000) แต่เก็บค่าเป็นตัวเลขล้วน
    const setMoney = (name) => (event) => (readOnly ? undefined : setRowField(row.key, name, parseNumberInput(event.target.value)));
    const canReorder = order.length > 1;
    const tag = incomeTagColor(readOnly ? DEFAULT_INCOME_TAG_COLOR : values.color);
    const expenseOptions = [
      ...(type?.allow_standard_expense ? [{ value: "standard", label: `หักแบบเหมา ${rate}%` }] : []),
      { value: "actual", label: "หักตามจริง" },
    ];
    const dragProps = canReorder
      ? {
          draggable: armedKey === key,
          onDragStart: (event) => handleDragStart(event, key),
          onDragOver: (event) => handleDragOver(event, key),
          onDrop: (event) => handleDrop(event, key),
          onDragEnd: handleDragEnd,
        }
      : {};

    return (
      <div
        className="px-3 py-2 mb-2"
        style={{
          ...rowCardStyle,
          ...(readOnly ? { background: "#f6f8fa" } : { background: tag.soft }),
          opacity: drag.key === key ? 0.4 : 1,
          ...dropIndicatorStyle(key),
        }}
        key={key}
        {...dragProps}
      >
        <div className="d-flex align-items-start gap-2">
          {/* รางซ้าย: เลขลำดับ + ตัวจัดลำดับ (หรือกุญแจถ้าแก้ไม่ได้) */}
          <div className="d-flex flex-column align-items-center flex-shrink-0 pt-1" style={{ width: 26 }}>
            <span
              className="rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold"
              style={{
                width: 22,
                height: 22,
                fontSize: "0.7rem",
                background: readOnly ? "#e2e8ec" : RD_GREEN_SOFT,
                color: readOnly ? "#6b8198" : RD_GREEN_DARK,
              }}
            >
              {index + 1}
            </span>
            {canReorder ? (
              <span
                className="text-secondary mt-1"
                style={{ cursor: "grab", fontSize: "0.8rem" }}
                title="ลากเพื่อจัดลำดับ"
                onMouseDown={() => setArmedKey(key)}
                onMouseUp={() => setArmedKey("")}
              >
                <i className="fa-solid fa-grip-vertical" />
              </span>
            ) : null}

            {/* แท็กสีของการ์ด: วงกลมเล็กใต้ตัวลาก คลิกแล้วเลือกสีจากเมนู (ค่าเริ่มต้นสีขาว = ไม่ติดแท็ก) */}
            {readOnly ? null : (
              <div className="position-relative mt-1" onMouseDown={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="rounded-circle d-block p-0"
                  style={{ width: 13, height: 13, background: tag.dot, border: `1px solid ${tag.border}`, cursor: "pointer" }}
                  title={`แท็กสี: ${tag.label}`}
                  onClick={() => setColorMenuKey((prev) => (prev === key ? "" : key))}
                />
                {colorMenuKey === key ? (
                  <div
                    className="position-absolute bg-white rounded-3 p-2 shadow-sm"
                    style={{ top: 20, left: 0, zIndex: 30, border: "1px solid #e2e8ec", width: 112 }}
                  >
                    <div className="d-flex flex-wrap gap-2">
                      {INCOME_TAG_COLORS.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          className="rounded-circle p-0"
                          style={{
                            width: 18,
                            height: 18,
                            background: item.dot,
                            border: `2px solid ${(values.color || DEFAULT_INCOME_TAG_COLOR) === item.value ? RD_GREEN_DARK : item.border}`,
                            cursor: "pointer",
                          }}
                          title={item.label}
                          onClick={() => {
                            setRowField(row.key, "color", item.value);
                            setColorMenuKey("");
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex-grow-1">
            {/* บรรทัดที่ 1: ใครจ่าย เมื่อไหร่ เท่าไหร่ */}
            <div className="row g-2 align-items-end">
              <Field label="ชื่อผู้จ่ายเงินได้ / ลูกค้า" col="col-12 col-md-6 col-lg-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={inputStyle}
                  value={values.payer_name || ""}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={set("payer_name")}
                />
              </Field>
              <Field label="เลขผู้จ่ายเงินได้" col="col-6 col-md-3 col-lg-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={inputStyle}
                  placeholder={readOnly ? "" : "13 หลัก"}
                  value={values.payer_tax_id || ""}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={set("payer_tax_id")}
                />
              </Field>
              <Field label="วันที่รับเงิน" col="col-6 col-md-3 col-lg-2">
                <DateInput
                  className="form-control form-control-sm"
                  style={inputStyle}
                  value={values.income_date || ""}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={set("income_date")}
                />
              </Field>
              <Field label="เงินได้ทั้งหมด" col="col-6 col-md-6 col-lg">
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-sm text-end"
                  style={inputStyle}
                  placeholder="0.00"
                  value={formatNumberInput(values.amount)}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={setMoney("amount")}
                />
              </Field>
              <Field
                col="col-6 col-md-6 col-lg"
                label={
                  <>
                    <span className="text-nowrap">ภาษีหัก ณ ที่จ่าย</span>
                    {readOnly ? null : (
                      <span className="ms-auto d-flex gap-1 ps-2">
                        {WHT_QUICK_RATES.map((quickRate) => (
                          <button
                            key={quickRate}
                            type="button"
                            className="btn btn-link p-0 text-decoration-none lh-1"
                            style={{ fontSize: "0.65rem", color: RD_GREEN_DARK }}
                            title={`คำนวณ ${quickRate}% ของเงินได้`}
                            onClick={() => setRowField(row.key, "wht_amount", ((num(row.amount) * quickRate) / 100).toFixed(2))}
                          >
                            {quickRate}%
                          </button>
                        ))}
                      </span>
                    )}
                  </>
                }
              >
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control form-control-sm text-end"
                  style={inputStyle}
                  placeholder="0.00"
                  value={formatNumberInput(values.wht_amount)}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={setMoney("wht_amount")}
                />
              </Field>
            </div>

            {/* บรรทัดที่ 2: ค่าใช้จ่าย + คำอธิบาย */}
            <div className="row g-2 align-items-end mt-0">
              {type?.allow_actual_expense ? (
                <Field label="วิธีหักค่าใช้จ่าย" col="col-12 col-md-4 col-lg-3">
                  <Select
                    className="form-control form-control-sm"
                    style={inputStyle}
                    value={values.expense_mode || "standard"}
                    options={expenseOptions}
                    default={false}
                    disabled={readOnly}
                    onChange={(value) => !readOnly && value && setRowField(row.key, "expense_mode", value)}
                  />
                </Field>
              ) : null}
              {values.expense_mode === "actual" ? (
                <Field label="ค่าใช้จ่ายตามจริง" hint={readOnly ? "" : "ต้องมีหลักฐานครบทุกใบ"} col="col-6 col-md-4 col-lg-2">
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm text-end"
                    style={inputStyle}
                    value={values.expense_amount}
                    disabled={readOnly}
                    readOnly={readOnly}
                    onChange={set("expense_amount")}
                  />
                </Field>
              ) : null}
              <Field label="รายละเอียด (บรรทัดที่ 2 ในตารางตรวจสอบข้อมูล)" col="col-12 col-md col-lg">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={inputStyle}
                  maxLength={200}
                  placeholder={readOnly ? "" : "เช่น ค่าออกแบบเว็บไซต์ งวดที่ 1"}
                  value={values.detail || ""}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={set("detail")}
                />
              </Field>
              <Field label="หมายเหตุ" col="col-12 col-md col-lg">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={inputStyle}
                  value={values.note || ""}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onChange={set("note")}
                />
              </Field>
            </div>
          </div>

          {readOnly ? (
            <span
              className="badge rounded-pill flex-shrink-0 mt-1 fw-medium"
              style={{ background: "#e2e8ec", color: "#465a6b", fontSize: "0.65rem" }}
              title="ล็อกเฉพาะข้อมูล แก้ไขที่รอบเงินเดือน ลำดับยังลากย้ายได้"
            >
              <i className="fa-solid fa-lock me-1" />{sourceLabel}
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-link p-0 flex-shrink-0 mt-1"
              style={{ color: "#c0c9d2", fontSize: "0.85rem" }}
              title="ลบรายการนี้"
              onClick={() => removeRow(row.key)}
            >
              <i className="fa-regular fa-trash-can" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <button type="button" className="btn btn-link p-0 text-decoration-none mb-3" style={{ color: RD_GREEN }} onClick={onBack}>
        <i className="fa-solid fa-arrow-left me-2" />กลับไปเลือกเงินได้
      </button>

      <h5 className="fw-semibold mb-3">
        {type?.label} (มาตรา {type?.section})
      </h5>

      <div className="rounded-3 p-3 mb-4" style={{ background: RD_GREEN_SOFT }}>
        <div className="fw-semibold mb-1" style={{ color: RD_GREEN_DARK }}>
          <i className="fa-solid fa-bullhorn me-2" />กรณีมีรายการเงินได้มากกว่า 1 รายการ
        </div>
        <ul className="mb-0 ps-4 small" style={{ color: "#12314d" }}>
          <li>เลือก เพิ่มรายการอื่น และกรอกรายละเอียด</li>
          <li>ในแบบแสดงรายการ จะแสดงเฉพาะผลรวมของเงินได้ทั้งหมด และเลขผู้จ่ายเงินได้จะแสดงเฉพาะผู้ที่จ่ายสูงสุด</li>
          {type?.hint ? <li>{type.hint}</li> : null}
        </ul>
      </div>

      <div className="p-3 p-md-4 mb-3" style={formBlockStyle}>
        <div className="fw-semibold mb-3">ส่วนของผู้มีเงินได้</div>

        {payrollRows.length > 0 ? (
          <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: "0.72rem", color: "#6b8198" }}>
            <i className="fa-solid fa-lock" />
            รายการสีเทาคือรายการจากสลิปเงินเดือน / ประมาณการ ข้อมูลแก้ที่นี่ไม่ได้ (แก้ที่รอบเงินเดือน) แต่ลากย้ายลำดับได้
          </div>
        ) : null}

        {order.map((key, index) => {
          const payrollItem = payrollByKey[key];
          if (payrollItem) {
            return renderCard({
              key,
              index,
              readOnly: true,
              sourceLabel: payrollItem.source === "projection" ? "ประมาณการ" : "สลิปเงินเดือน",
              values: {
                payer_name: payrollItem.payer_name || "",
                payer_tax_id: payrollItem.payer_tax_id || "",
                income_date: payrollItem.income_date ? String(payrollItem.income_date).slice(0, 10) : "",
                amount: payrollItem.amount ?? "",
                wht_amount: payrollItem.wht_amount ?? "",
                expense_mode: payrollItem.expense_mode || "standard",
                expense_amount: payrollItem.expense_amount ?? "",
                detail: payrollItem.label || "",
                note: payrollItem.note || "",
              },
            });
          }
          const row = rowByKey[key];
          return row ? renderCard({ key, index, row, values: row }) : null;
        })}

        <button
          type="button"
          className="btn w-100 rounded-3 mb-3 py-2 fw-medium"
          style={{ border: `1px dashed ${RD_GREEN}`, color: RD_GREEN_DARK, background: "#fbfdfb", fontSize: "0.9rem" }}
          onClick={addRow}
        >
          <i className="fa-solid fa-plus me-2" />เพิ่มรายการอื่น
        </button>

        <div className="d-flex flex-wrap justify-content-end gap-4 px-1 small">
          <div>
            <span className="text-secondary">รวมเงินได้ทั้งหมด</span>
            <b className="ms-2">{toFixed(gross)}</b>
          </div>
          <div>
            <span className="text-secondary">รวมภาษีหัก ณ ที่จ่าย</span>
            <b className="ms-2">{toFixed(payrollWht + manualWht)}</b>
          </div>
        </div>
      </div>

      <button type="button" className="btn btn-link p-0 text-decoration-none mb-3" style={{ color: RD_GREEN }} onClick={() => setShowCalc((prev) => !prev)}>
        ดูรายละเอียดการคำนวณ <i className={`fa-solid fa-chevron-${showCalc ? "up" : "down"} ms-1`} style={{ fontSize: "0.75rem" }} />
      </button>

      {showCalc && (
        <div className="p-3 mb-3" style={formBlockStyle}>
          <div className="small mb-1">เงินได้ทั้งหมด: <b>{toFixed(gross)}</b></div>
          <div className="small mb-1">
            หักค่าใช้จ่ายแบบเหมา {rate}%{cap ? ` (ไม่เกิน ${toFixed(cap, 0)} บาท)` : ""}: <b>{toFixed(standardExpense)}</b>
          </div>
          <div className="small mb-1">คงเหลือเงินได้หลังหักค่าใช้จ่าย: <b>{toFixed(gross - standardExpense)}</b></div>
          <div className="small text-secondary mt-2">
            ตัวเลขนี้คิดเฉพาะเงินได้ประเภทนี้ ยอดจริงจะรวมเพดานค่าใช้จ่ายร่วมกับเงินได้ประเภทอื่นให้ในขั้นตอนตรวจสอบข้อมูล
          </div>
        </div>
      )}

      <div className="d-flex align-items-center gap-3">
        <button type="button" className="btn btn-success px-4 rounded-3" onClick={onBack}>
          <i className="fa-solid fa-arrow-left me-2" />กลับไปเลือกเงินได้
        </button>
        <span className="small text-secondary">ระบบบันทึกให้อัตโนมัติเมื่อหยุดพิมพ์</span>
      </div>
    </>
  );
}
